import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import type { UserRole } from '../types';

interface AuthContextType {
  user: FirebaseUser | null;
  role: UserRole | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  isSuperAdmin: boolean;
  isAppAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);

      if (firebaseUser) {
        // Check if user email is from wifirst.fr domain
        if (!firebaseUser.email?.endsWith('@wifirst.fr')) {
          await firebaseSignOut(auth);
          setUser(null);
          setRole(null);
          setLoading(false);
          throw new Error('Only @wifirst.fr emails are allowed');
        }

        // Get user role from custom claims
        try {
          const tokenResult = await firebaseUser.getIdTokenResult();
          const userRole = tokenResult.claims.role as UserRole | undefined;
          setRole(userRole || 'viewer');
        } catch (error) {
          console.error('Error fetching user role:', error);
          setRole('viewer'); // Default role
        }
      } else {
        setRole(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
    } catch (error: any) {
      console.error('Error signing in:', error);
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign in cancelled');
      } else if (error.code === 'auth/unauthorized-domain') {
        throw new Error('Unauthorized domain');
      } else {
        throw new Error('Failed to sign in with Google');
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw new Error('Failed to sign out');
    }
  };

  const value = {
    user,
    role,
    loading,
    signInWithGoogle,
    signOut,
    isSuperAdmin: role === 'superadmin',
    isAppAdmin: role === 'app_admin',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
